/**
 * 客服记录
 */
import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Button,
  Tag,
  message,
  Popconfirm,
  Input,
  Select,
  Radio,
  Modal,
} from "antd";
import dayjs from "dayjs";
import {
  serviceRecoedRequest,
  deleteServiceRecoed,
  serviceRecoedRequestPath,
  addServiceRecoed,
} from "@/service/carDiagnosis";
import CategoryTreeSelect from "@/components/categoryTreeSelect";
import CommonTable from "@/components/commonTable";
import ExportButton from "@/components/exportButton";
import DatePicker from "@/components/datePicker";
import Thumbnail from "@/components/thumbnail";
import { operateDayRange } from "@/utils/operateSearchParams";
import { useUserCateIdArr } from "@/utils/ownHooks";
import { useTable } from "@/utils/useTable";
import styles from "@/styles/pageContent.module.scss";
import PermissionButton from "@/components/permissionButton";
import OperateService, { concatResultList } from "@/pages/carDiagnosis/components/operateService";
import { tagListRequest, auditStatusEnumRequest } from "@/service/tagManage";
import { TagCategoryEnum } from "@/constants/tagManage";

const { RangePicker } = DatePicker;
const ServiceRecord = () => {
  const cateIdArr = useUserCateIdArr();
  const [searchParams, setSearchParams] = useState({
    beginDate: `${dayjs().subtract(7, "day").format("YYYY-MM-DD")}`,
    endDate: `${dayjs().subtract(1, "day").format("YYYY-MM-DD")}`,
    cateId: cateIdArr,
  }); // 搜索相关的参数
  const [operateVisible, setOperateVisible] = useState<any>({}); // 添加客服记录的弹窗
  const [form] = Form.useForm();
  const [auditRemark, setAuditRemark] = useState("");
  const [carRelationResult, setCarRelationResult] = useState<any>([]); // 车辆联系结果
  const [approvalStatusOptions, setApprovalStatusOptions] = useState<any>([]); // 审核状态选项
  const [auditVisible, setAuditVisible] = useState<any>({}); // 审核弹窗
  const [auditResult, setAuditResult] = useState<any>(undefined); // 审核结果
  const [auditLoading, setAuditLoading] = useState(false); // 审核提交loading
  const { page, limit, loading, data, totalCount, onPageChange, getData } =
    useTable({
      requestFunc: serviceRecoedRequest,
      initPageSize: 40,
      searchParams,
    });

  useEffect(() => {
    getCarRelationResult();
    getApprovalStatusOptions();
  }, []);

  const [auditDispatch, setAuditDispatch] = useState<number | undefined>(undefined);

  // 搜索
  const onSearch = async (values: any) => {
    const newSearchParams = operateDayRange(values);
    setSearchParams(newSearchParams);
  };

  // 获取导出的参数
  const getExportParams = () => {
    const values = form.getFieldsValue();
    return operateDayRange(values, true);
  };

  // 删除客服记录
  const deleteServiceRecoedItem = async (id: number) => {
    const res = await deleteServiceRecoed(id);
    if (res.data?.code === 0) {
      message.success("删除成功");
      getData();
    }
  };

  // 审核提交
  const resetAuditModal = () => {
    setAuditVisible({});
    setAuditResult(undefined);
    setAuditRemark("");
    setAuditDispatch(undefined);
  };

  const getApprovalStatusValue = (label: string) =>
    approvalStatusOptions.find((opt: any) => opt.label === label)?.value;
  const normalizeText = (value: any) => String(value || "").replace(/\s+/g, "");
  const matchApprovalStatusOption = (opt: any, labels: string[]) =>
    labels.some((label) => normalizeText(label) === normalizeText(opt?.label));
  const isApprovePassStatus = (value: any) =>
    approvalStatusOptions.some(
      (opt: any) =>
        opt?.value === value &&
        matchApprovalStatusOption(opt, ["审批通过", "审核通过", "APPROVED"]),
    );
  const isApproveRejectStatus = (value: any) =>
    approvalStatusOptions.some(
      (opt: any) =>
        opt?.value === value &&
        matchApprovalStatusOption(opt, ["审批拒绝", "审核不通过", "审批不通过", "REJECTED"]),
    );

  const openAuditModal = (item: any) => {
    if (item.approvalStatus !== getApprovalStatusValue("待审批")) {
      message.warning("该客服记录已审核，无法重复审核");
      return;
    }
    setAuditVisible({ show: true, data: item });
    setAuditResult(undefined);
    setAuditRemark(item?.remark || "");
    setAuditDispatch(item?.dispatch ?? undefined);
  };

  const handleAuditSubmit = async () => {
    if (auditResult === undefined) {
      message.warning("请选择审核结果");
      return;
    }
    if (isApproveRejectStatus(auditResult) && auditDispatch === undefined) {
      message.warning("审批不通过时请选择是否派发任务");
      return;
    }
    setAuditLoading(true);
    try {
      const item = auditVisible.data;
      const params: any = {
        ...item,
        approvalStatus: auditResult,
        remark: auditRemark,
        dispatch: auditDispatch,
      };
      if (!isApproveRejectStatus(auditResult) && auditDispatch === undefined) {
        delete params.dispatch;
      }
      // 将 viewResources 转换为 resources 格式，保留原有上传文件
      if (Array.isArray(item.viewResources) && item.viewResources.length > 0) {
        params.resources = item.viewResources.map((v: any) => ({
          contentType: v.contentType,
          key: v.key,
          bucket: v.bucket ?? null,
        }))
      }
      delete params.viewResources;
      const res = await addServiceRecoed(params);
      if (res.data?.code === 0) {
        message.success("审核成功");
        resetAuditModal();
        getData();
      }
    } catch (error) {
      console.log(error);
    } finally {
      setAuditLoading(false);
    }
  };

  // 获取车辆联系结果
  const getCarRelationResult = async () => {
    const res = await tagListRequest({
      tagSorts: [TagCategoryEnum.carRelationResult],
    });
    const tags: any = [];
    (res.data?.data || []).forEach((item: any) => {
      const { title } = item;
      tags.push({ label: title, value: title });
    });
    setCarRelationResult(tags);
  };

  // 获取审核状态枚举
  const getApprovalStatusOptions = async () => {
    const res = await auditStatusEnumRequest();
    const options = (res.data?.data || []).map((item: any) => ({
      label: item.first,
      value: item.second,
    }));
    setApprovalStatusOptions(options);
  };

  //处理状态
  const manageType = (value: any) => {
    if (value == 0) {
      return "关闭";
    } else if (value == 1) {
      return "未创建";
    } else if (value == 2) {
      return "已创建";
    } else if (value == 3) {
      return "已派单";
    }
  };

  const anyData = () => { };

  // 表格列显示
  const columns = [
    { title: "序号", dataIndex: "taskId", width: 80 },
    {
      title: "车牌号",
      dataIndex: "plateNumber",
      width: 110,
    },
    {
      title: "车架号",
      dataIndex: "frameNumber",
      width: 110,
    },
    {
      title: "客服",
      dataIndex: "nickName",
      width: 110,
    },
    {
      title: "问题来源",
      dataIndex: "category",
      width: 110,
    },
    {
      title: "联系结果",
      dataIndex: "result",
      width: 110,
    },
    {
      title: "联系电话",
      dataIndex: "tel",
      width: 110,
    },
    {
      title: "客户电话",
      dataIndex: "customerPhone",
      width: 110,
    },
    {
      title: "客户身份",
      dataIndex: "customerIdentity",
      width: 110,
    },
    {
      title: "沟通类型",
      dataIndex: "classify",
      width: 110,
    },
    // {
    //   title: '处理状态',
    //   dataIndex: 'state',
    //   width: 110,
    //   render: (value: number) => manageType(value),
    // },
    {
      title: "问题原因",
      dataIndex: "tags",
      width: 130,
      ellipsis: { showTitle: false },
      render: (value: any) => (
        <div className={styles.tableTagColumn}>
          {((Object.values(value || {})[0] as any) || []).map((item1: any) => (
            <Tag key={item1.tagId}>{item1.title}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: "记录内容",
      dataIndex: "content",
      width: 150,
      render: (value: string) => (
        <span style={{ whiteSpace: "pre-wrap" }}>{value}</span>
      ),
    },
    {
      title: "用户配合度",
      dataIndex: "adaptability",
      width: 150,
    },
    {
      title: "客服处理方式",
      dataIndex: 'dealType',
      width: 100,
      ellipsis: { showTitle: false },
      render: (value: any) => {
        if (value === 0) return '人工';
        if (value === 1) return '智能';
        return value;
      },
    },
    {
      title: "创建日期",
      dataIndex: "createdAt",
      width: 150,
      render: (value: string) =>
        value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : value,
    },
    {
      title: "审核状态",
      dataIndex: "approvalStatus",
      width: 110,
      render: (value: any) => {
        const matched = approvalStatusOptions.find((item: any) => item.value === value);
        return matched ? matched.label : value;
      },
    },
    {
      title: "续管车客服是否介入",
      dataIndex: "interventionFlag",
      width: 150,
    },
    {
      title: "录音",
      dataIndex: "recordingUrl",
      width: 300,
      render: (value: string) => (value ? <audio controls src={value}></audio> : "无"),
    },
    {
      title: "客服记录图片/视频",
      dataIndex: "viewResources",
      width: 150,
      ellipsis: { showTitle: false },
      render: (value: any) => (
        <Thumbnail dataSource={value || []} downloadImgName="客服图片" />
      ),
    },
    {
      title: "操作",
      dataIndex: "operate",
      width: 120,
      // fixed: 'right' as 'right',
      render: (value: any, item: any) => {
        return (
          <div className={styles.tableOperateColumn}>
            <PermissionButton
              type="link"
              operateType="/car-admin/car-diagnosis/service-recoed-edit"
              onClick={() =>
                setOperateVisible({ show: true, type: "edit", data: item })
              }
            >
              编辑
            </PermissionButton>
            {item.approvalStatus === getApprovalStatusValue('待审批') && (
              <PermissionButton
                operateType="/car-admin/car-diagnosis/verify"
                type="link"
                onClick={() => openAuditModal(item)}
              >
                审核
              </PermissionButton>
            )}
            <Popconfirm
              title="您确定要删除该客服记录吗?"
              onConfirm={() => deleteServiceRecoedItem(item.id)}
            >
              <PermissionButton
                operateType="/car-admin/car-diagnosis/service-recoed-delete"
                type="link"
              >
                删除
              </PermissionButton>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <Card bordered={false} className={styles.fixPageContentBox}>
      <Form onFinish={onSearch} form={form}>
        <Row gutter={24}>
          <Col span={6}>
            <Form.Item
              label="日期范围"
              name="time"
              initialValue={[
                dayjs(searchParams.beginDate),
                dayjs(searchParams.endDate),
              ]}
            >
              <RangePicker allowClear={false} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="分类选择"
              name="cateId"
              initialValue={searchParams.cateId}
            >
              <CategoryTreeSelect treeCheckable />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="序号" name="taskId">
              <Input
                placeholder="请输入序号"
                allowClear
                maxLength={10}
                onChange={(e) => {
                  const newV = e.target.value.replace(/\D/g, "");
                  form.setFieldValue("taskId", newV || undefined);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="车牌号" name="plateNumber">
              <Input
                placeholder="请输入车牌号"
                allowClear
                onChange={(e) => {
                  const newV = e.target.value.replace(/\s+/g, "");
                  form.setFieldValue("plateNumber", newV);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="车架号" name="frameNumber">
              <Input
                placeholder="请输入车架号"
                allowClear
                onChange={(e) => {
                  const newV = e.target.value.replace(/\s+/g, "");
                  form.setFieldValue("frameNumber", newV);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={6}>
            <Form.Item label="联系结果" name="result">
              <Select
                placeholder="请选择联系结果"
                options={concatResultList}
                allowClear
                showArrow
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="联系电话" name="tel">
              <Input placeholder="请输入联系电话" allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="客户电话" name="customerPhone">
              <Input
                placeholder="请输入客户电话"
                allowClear
                onChange={(e) => {
                  const newV = e.target.value.replace(/\s+/g, "");
                  form.setFieldValue("customerPhone", newV);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="客户身份" name="customerIdentity">
              <Input
                placeholder="请输入客户身份"
                allowClear
                onChange={(e) => {
                  const newV = e.target.value.replace(/\s+/g, "");
                  form.setFieldValue("customerIdentity", newV);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={6}>
            <Form.Item label="用户配合度" name="adaptability">
              <Select
                placeholder="请选择用户配合度"
                options={[
                  { name: "配合", value: "配合" },
                  { name: "一般配合", value: "一般配合" },
                  { name: "完全不配合", value: "完全不配合" },
                ]}
                allowClear
                showArrow
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="沟通类型"
              name="classify"
            // rules={[{ required: true, message: "请选择沟通类型" }]}
            >
              <Select
                placeholder="请选择沟通类型"
                allowClear
                options={[
                  { label: "日常沟通", value: "日常沟通" },
                  { label: "事故沟通", value: "事故沟通" },
                  { label: "减/免责情况沟通", value: "减/免责情况沟通" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="审核状态" name="approvalStatus">
              <Select
                placeholder="请选择审核状态"
                options={approvalStatusOptions}
                allowClear
                showArrow
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item label="客服处理方式" name="dealType">
              <Select
                options={[
                  { label: '人工', value: 0 },
                  { label: '智能', value: 1 }
                ]}
                placeholder="请选择处理方式"
                allowClear
                showArrow
                showSearch
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item>
              <div className={styles.searchBox}>
                <Button type="primary" htmlType="submit">
                  搜索
                </Button>
                <ExportButton
                  url={serviceRecoedRequestPath}
                  fileName="客服记录"
                  getParamsFunc={getExportParams}
                  requestMethod="GET"
                  permissionKey="/car-admin/car-diagnosis/service-recoed-export"
                />
                <PermissionButton
                  type="primary"
                  operateType="/car-admin/car-diagnosis/service-recoed-add"
                  style={{ marginBottom: "16px" }}
                  onClick={() =>
                    setOperateVisible({ show: true, type: "serviceRecordAdd" })
                  }
                >
                  新增客服记录
                </PermissionButton>
              </div>
            </Form.Item>
            {/* <Form.Item>
              <div className={styles.operateBox}>
                <PermissionButton
                  type="primary"
                  operateType="/car-admin/car-diagnosis/service-recoed-add"
                  style={{ marginBottom: "16px" }}
                  onClick={() =>
                    setOperateVisible({ show: true, type: "serviceRecordAdd" })
                  }
                >
                  新增客服记录
                </PermissionButton>
              </div>
            </Form.Item> */}
          </Col>
        </Row>
      </Form>
      <CommonTable
        columns={columns}
        dataSource={data}
        pagination={{
          total: totalCount,
          current: page,
          pageSize: limit,
          onChange: onPageChange,
        }}
        bordered
        loading={loading}
      />
      {/* 添加客服记录的弹窗 */}
      {operateVisible.show && (
        <OperateService
          type={operateVisible.type}
          onClose={() =>
            setOperateVisible({ show: false, type: "serviceRecordAdd" })
          }
          reloadData={() => getData()}
          anyData={anyData}
          editData={operateVisible.data}
        />
      )}
      {/* 审核弹窗 */}
      <Modal
        title="审核"
        open={auditVisible.show}
        onCancel={resetAuditModal}
        onOk={handleAuditSubmit}
        confirmLoading={auditLoading}
        maskClosable={false}
      >
        <Form layout="vertical">
          <Form.Item label="审核结果" required>
            <Radio.Group
              value={auditResult}
              onChange={(e) => setAuditResult(e.target.value)}
            >
              {approvalStatusOptions
                .filter((opt: any) => isApprovePassStatus(opt.value) || isApproveRejectStatus(opt.value))
                .map((opt: any) => (
                  <Radio key={opt.value} value={opt.value}>
                    {opt.label}
                  </Radio>
                ))}
            </Radio.Group>
          </Form.Item>
          <Form.Item label="是否派发任务" required={isApproveRejectStatus(auditResult)}>
            <Radio.Group
              value={auditDispatch}
              onChange={(e) => setAuditDispatch(e.target.value)}
            >
              <Radio value={1}>是</Radio>
              <Radio value={0}>否</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="备注">
            <Input.TextArea
              value={auditRemark}
              onChange={(e) => setAuditRemark(e.target.value)}
              placeholder="请输入备注（非必填）"
              rows={4}
              allowClear
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ServiceRecord;
